// src/pages/Admin/Settings/AdminProfile.jsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { FaUser, FaLock, FaSave, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Common/Sidebar';
import { toast } from 'react-toastify';

const AdminProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
    full_name: '',
    phone: ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [profileUpdating, setProfileUpdating] = useState(false);
  const [passwordUpdating, setPasswordUpdating] = useState(false);

  useEffect(() => {
    // Get user from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setProfileForm({
        username: userData.username || '',
        email: userData.email || '',
        full_name: userData.full_name || '',
        phone: userData.phone || ''
      });
    }
  }, []);

  const handleProfileChange = (e) => {
    setProfileForm({
      ...profileForm,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setProfileUpdating(true);
      
      // TODO: Implement profile update API call
      // await adminAPI.updateProfile(profileForm);
      
      // Update localStorage
      const updatedUser = { ...user, ...profileForm };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Error:', error);
    } finally {
      setProfileUpdating(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setPasswordUpdating(true);
      
      // TODO: Implement password change API call
      // await adminAPI.changePassword({
      //   current_password: passwordForm.current_password,
      //   new_password: passwordForm.new_password
      // });
      
      toast.success('Password changed successfully');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
      console.error('Error:', error);
    } finally {
      setPasswordUpdating(false);
    }
  };

  return (
   <div className="dashboard-layout">
  <Sidebar role="admin" />
  <div className="dashboard-content">

        <div className="mb-4">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => navigate('/admin/settings')}
            className="mb-3"
          >
            <FaArrowLeft className="me-2" />
            Back to Settings
          </Button>
          <h2 className="fw-bold mb-0">Admin Profile</h2>
          <p className="text-muted">Manage your account information and security</p>
        </div>

        <Row className="g-4">
          {/* Profile Information */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaUser className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  Profile Information
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form onSubmit={handleProfileSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username *</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={profileForm.username}
                      onChange={handleProfileChange}
                      required
                      disabled
                    />
                    <Form.Text className="text-muted">
                      Username cannot be changed
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Address *</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={profileForm.email}
                      onChange={handleProfileChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="full_name"
                      value={profileForm.full_name}
                      onChange={handleProfileChange}
                      placeholder="Enter your full name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={profileForm.phone}
                      onChange={handleProfileChange}
                      placeholder="+91 1234567890"
                    />
                  </Form.Group>

                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={profileUpdating}
                    className="w-100"
                  >
                    {profileUpdating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Update Profile
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Change Password */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaLock className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  Change Password
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Alert variant="info" className="mb-4">
                  <small>
                    <strong>Password Requirements:</strong>
                    <ul className="mb-0 mt-2 ps-3">
                      <li>Minimum 8 characters</li>
                      <li>Include uppercase and lowercase letters</li>
                      <li>Include at least one number</li>
                      <li>Include at least one special character</li>
                    </ul>
                  </small>
                </Alert>

                <Form onSubmit={handlePasswordSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Password *</Form.Label>
                    <Form.Control
                      type="password"
                      name="current_password"
                      value={passwordForm.current_password}
                      onChange={handlePasswordChange}
                      required
                      placeholder="Enter current password"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>New Password *</Form.Label>
                    <Form.Control
                      type="password"
                      name="new_password"
                      value={passwordForm.new_password}
                      onChange={handlePasswordChange}
                      required
                      placeholder="Enter new password"
                      minLength={8}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Confirm New Password *</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirm_password"
                      value={passwordForm.confirm_password}
                      onChange={handlePasswordChange}
                      required
                      placeholder="Confirm new password"
                      minLength={8}
                    />
                  </Form.Group>

                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={passwordUpdating}
                    className="w-100"
                  >
                    {passwordUpdating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Changing...
                      </>
                    ) : (
                      <>
                        <FaLock className="me-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Account Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h6 className="fw-bold mb-3">Account Information</h6>
                <Row>
                  <Col md={6}>
                    <table className="table table-borderless table-sm">
                      <tbody>
                        <tr>
                          <td className="text-muted">Account Type:</td>
                          <td className="text-end fw-bold">Admin</td>
                        </tr>
                        <tr>
                          <td className="text-muted">User ID:</td>
                          <td className="text-end fw-bold">#{user?.id}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Status:</td>
                          <td className="text-end">
                            <span className="badge bg-success">Active</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Col>
                  <Col md={6}>
                    <table className="table table-borderless table-sm">
                      <tbody>
                        <tr>
                          <td className="text-muted">Account Created:</td>
                          <td className="text-end">
                            {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Last Login:</td>
                          <td className="text-end">
                            {new Date().toLocaleString()}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Two-Factor Auth:</td>
                          <td className="text-end">
                            <span className="badge bg-secondary">Disabled</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AdminProfile;